import { mulberry32, type PipelineArtifacts } from '@/reference';

/**
 * The Flat Residential Lot template, authored as pipeline artifacts.
 *
 * "Flat Residential Lot" used to generate a flat rectangle: a template whose noise amplitude works
 * out to 24 mm produces ground indistinguishable from the blank canvas, and nothing else at all —
 * no house, no drive, no planting. The name promised a residential lot and the button delivered a
 * lawn.
 *
 * Rather than grow a second site generator beside the reconstruction one, this module authors the
 * same five artifacts a photograph would have produced and hands them to `compilePipeline`. Three
 * things follow, and the third is the reason it is worth doing this way:
 *
 * 1. The template gets everything the pipeline can do — sculpted grade, painted surfaces, polygon
 *    massing, spacing-aware planting — without a line of it being written twice.
 * 2. Any improvement to the compiler improves the template, and any bug shows up in both.
 * 3. It is a **worked example of the artifact schema** that runs on every build. When a stage prompt
 *    and the compiler disagree about what a field means, this is where it surfaces, in a test,
 *    instead of in a paid call.
 *
 * Fully deterministic from `seed`. Dimensions are fractions of the lot, so a 12 m courtyard and a
 * 60 m paddock both come out proportioned rather than clipped.
 */

export interface ResidentialLotOptions {
  widthMeters: number;
  lengthMeters: number;
  seed: number;
}

/**
 * The lot's geometry, in the world frame the compiler uses.
 *
 * The street is at `+Z`, so the house sits toward `-Z` behind its setback and the drive runs from
 * the front boundary to the garage — the arrangement that makes the front-to-back reading of a
 * residential lot legible from the default camera.
 */
export function residentialLotArtifacts(options: ResidentialLotOptions): PipelineArtifacts {
  const width = clamp(options.widthMeters, 12, 200);
  const length = clamp(options.lengthMeters, 15, 200);
  const rand = mulberry32(Math.max(1, Math.trunc(options.seed)));

  const hw = width / 2;
  const hl = length / 2;

  /*
   * Across the lot. The house block is sized proportionally and then *fitted*: on a narrow lot the
   * two masses shrink together rather than the garage sliding into the side boundary.
   */
  const sideMargin = clamp(width * 0.06, 0.8, 3);
  const maxBlock = Math.max(6, width - 2 * sideMargin - 0.6);

  let mainWidth = clamp(width * 0.42, 7, 14);
  let garageWidth = clamp(width * 0.25, 5.4, 7.2);
  if (mainWidth + garageWidth > maxBlock) {
    const scale = maxBlock / (mainWidth + garageWidth);
    mainWidth = Math.max(4.5, mainWidth * scale);
    garageWidth = Math.max(3.2, garageWidth * scale);
  }

  const blockWidth = mainWidth + garageWidth;
  // Nudge the house off dead centre — a lot with the house exactly centred reads as a diagram —
  // then clamp so the nudge can never push it into a boundary.
  const centreSlack = Math.max(0, hw - sideMargin - blockWidth / 2);
  const blockLeft =
    -blockWidth / 2 + Math.max(-centreSlack, Math.min(centreSlack, (rand() - 0.5) * width * 0.05));

  const mainX0 = blockLeft;
  const mainX1 = blockLeft + mainWidth;
  const garageX0 = mainX1;
  const garageX1 = mainX1 + garageWidth;

  /*
   * Down the lot, as an explicit depth budget rather than four independent fractions.
   *
   * Laying the bands out back-to-front and then scaling them to fit is what makes the template
   * survive a 16 m lot: computing each depth on its own and hoping they add up is how a rear border
   * ends up planted past the boundary, which is precisely what the earlier layout did.
   */
  const rearMargin = clamp(length * 0.06, 1, 3); // boundary screen band
  const minFrontYard = clamp(length * 0.16, 4, 13);

  let rearBed = clamp(length * 0.055, 1.2, 2.6);
  let patioDepth = clamp(length * 0.11, 2.5, 5.5);
  let mainDepth = clamp(length * 0.24, 5.5, 11);

  const flexible = rearBed + patioDepth + mainDepth;
  const available = length - rearMargin - minFrontYard - 0.3;
  if (flexible > available) {
    const scale = Math.max(0.45, available / flexible);
    rearBed *= scale;
    patioDepth *= scale;
    mainDepth *= scale;
  }

  const screenZ = -hl + rearMargin;
  const rearBedZ0 = screenZ + 0.4;
  const rearBedZ1 = rearBedZ0 + rearBed;
  const patioZ0 = rearBedZ1;
  const patioZ1 = patioZ0 + patioDepth;
  const mainZ0 = patioZ1 + 0.3;
  const houseFrontZ = mainZ0 + mainDepth;
  const setback = hl - houseFrontZ;

  const garageDepth = clamp(mainDepth * 0.8, 4.5, 9);
  const garageZ0 = houseFrontZ - garageDepth;

  const eave = clamp(length * 0.09, 2.5, 5.8);
  const garageEave = eave > 4 ? eave * 0.52 : eave * 0.92;

  const porchWidth = clamp(mainWidth * 0.42, 2.4, 5);
  const porchDepth = clamp(setback * 0.16, 1.1, 2.4);
  const porchCenter = (mainX0 + mainX1) / 2;

  const driveHalf = garageWidth / 2;
  const driveCenter = (garageX0 + garageX1) / 2;
  // The street flare cannot cross the side boundary, and on a narrow lot there is no room for one.
  const driveFlare = Math.max(0, Math.min(1.3, hw - (driveCenter + driveHalf) - 0.2));

  /*
   * Planting stand-off. A building refuses planting inside its footprint plus `clearanceMeters`, so
   * a bed drawn against the wall is a bed nothing can be planted in. Every foundation position is
   * measured from this, not from the wall.
   */
  const houseClearance = 0.8;
  const standoff = houseClearance + 0.75;
  const bedDepth = clamp(setback * 0.3, standoff + 0.6, 3.4);

  /*
   * Planting, sized from the ground each mass actually has.
   *
   * Counts are derived rather than written down, because a hard-coded count is a promise about a lot
   * size. Sixteen switchgrass in a 1.2 m band is not a dense border, it is eleven plants that failed
   * to place and a warning that the template disagrees with its own compiler — which is exactly what
   * a template meant to serve as a worked example must never do. Masses with no room are omitted
   * rather than asked for and refused.
   */
  const plantings: PipelineArtifacts['flora']['plantings'] = [];

  /*
   * Foundation shrubs, in two runs flanking the porch.
   *
   * Not one run across the whole frontage, for two reasons that are the same reason: the porch has
   * its own keep-out and the garage front is the drive. A single line through both is a line whose
   * middle third cannot be planted — which the compiler correctly refuses and then warns about.
   * Splitting it here is not a workaround, it is what the planting actually is.
   */
  const porchGap = standoff + 0.5;
  const bedZ = houseFrontZ + standoff;
  for (const [id, x0, x1] of [
    ['planting-foundation-west', mainX0 + 0.2, porchCenter - porchWidth / 2 - porchGap],
    ['planting-foundation-east', porchCenter + porchWidth / 2 + porchGap, mainX1 - 0.4],
  ] as const) {
    if (x1 - x0 < 1.4) continue;
    plantings.push({
      id,
      name: 'Foundation shrubs',
      speciesId: 'ilex-glabra',
      layer: 'shrub',
      minSpacingMeters: 1.1,
      maturity: [0.55, 0.8],
      group: 'Foundation planting',
      line: {
        points: [
          [x0, bedZ],
          [x1, bedZ],
        ],
        spacingMeters: 1.25,
        jitterMeters: 0.1,
      },
    });
  }

  /*
   * A hydrangea pair at the house's outer corner — the lawn side, where they can reach their 2 m
   * spread without meeting the drive.
   *
   * A short line rather than a drift: two plants that must be 1.5 m apart inside a 1.2 m drift is a
   * request the placement engine can only sometimes satisfy, and "sometimes" in a template means a
   * warning that appears at some lot sizes and not others.
   */
  const cornerX = mainX0 - 1.6;
  if (cornerX - 0.8 > -hw + sideMargin) {
    plantings.push({
      id: 'planting-corner-accent',
      name: 'Corner hydrangeas',
      speciesId: 'hydrangea-paniculata',
      layer: 'shrub-accent',
      minSpacingMeters: 1.5,
      maturity: [0.6, 0.85],
      group: 'Foundation planting',
      line: {
        points: [
          [cornerX, bedZ],
          [cornerX, bedZ + 1.7],
        ],
        spacingMeters: 1.7,
      },
    });
  }

  // A street tree on the side of the frontage the drive does not take.
  const streetTreeX = -hw + sideMargin + 1.8;
  if (streetTreeX < driveCenter - driveHalf - 3 && setback > 5) {
    plantings.push({
      id: 'planting-street-tree',
      name: 'Street tree',
      speciesId: 'acer-rubrum',
      layer: 'canopy',
      minSpacingMeters: 9,
      maturity: [0.5, 0.7],
      group: 'Street trees',
      drifts: [{ x: streetTreeX, z: hl - Math.min(3.2, setback * 0.3), radiusMeters: 1, count: 1 }],
    });
  }

  // A front specimen in the lawn between the house and the side boundary, when that gap is real.
  const specimenX = (mainX0 + (-hw + sideMargin)) / 2;
  if (mainX0 - (-hw + sideMargin) > 4 && setback > 4.5) {
    plantings.push({
      id: 'planting-specimen',
      name: 'Front specimen',
      speciesId: 'cornus-florida',
      layer: 'understory',
      minSpacingMeters: 5,
      maturity: [0.55, 0.75],
      group: 'Front garden',
      drifts: [{ x: specimenX, z: houseFrontZ + Math.min(bedDepth + 2, setback * 0.5), radiusMeters: 0.9, count: 1 }],
    });
  }

  plantings.push({
    id: 'planting-screen',
    name: 'Rear boundary screen',
    speciesId: 'thuja-occidentalis',
    layer: 'conifer',
    minSpacingMeters: 1.5,
    maturity: [0.6, 0.9],
    group: 'Rear boundary',
    linked: true,
    pruningStyle: 'natural',
    line: {
      points: [
        [-hw + sideMargin, screenZ],
        [hw - sideMargin, screenZ],
      ],
      spacingMeters: 1.7,
      jitterMeters: 0.15,
    },
  });

  // The rear border is a band, so its count comes from the band's own area.
  const borderX0 = mainX0 - 0.4;
  const borderX1 = mainX1 + 0.4;
  const borderBand = { x0: borderX0, x1: borderX1, z0: rearBedZ0, z1: rearBedZ1 };
  const borderArea = Math.max(0, (borderX1 - borderX0) * (rearBedZ1 - rearBedZ0));

  const grassCount = fitCount(borderArea * 0.55, 0.85, 22);
  if (grassCount >= 3) {
    plantings.push({
      id: 'planting-rear-bed',
      name: 'Rear border grasses',
      speciesId: 'panicum-virgatum',
      layer: 'grass',
      minSpacingMeters: 0.85,
      maturity: [0.55, 0.95],
      group: 'Rear border',
      regions: [borderBand],
      count: grassCount,
    });
  }

  const perennialCount = fitCount(borderArea * 0.45, 0.42, 26);
  if (perennialCount >= 4) {
    plantings.push({
      id: 'planting-perennials',
      name: 'Border perennials',
      speciesId: 'echinacea-purpurea',
      layer: 'perennial',
      minSpacingMeters: 0.42,
      maturity: [0.6, 1],
      group: 'Rear border',
      regions: [borderBand],
      count: perennialCount,
    });
  }

  // Groundcover in the side yard beside the garage, only where a side yard exists.
  const sideRoom = hw - sideMargin - garageX1;
  if (sideRoom > 2.4) {
    const radius = Math.min(sideRoom / 2 - 0.2, 2);
    plantings.push({
      id: 'planting-side-groundcover',
      name: 'Side groundcover',
      speciesId: 'juniperus-horizontalis',
      layer: 'groundcover',
      minSpacingMeters: 1.3,
      maturity: [0.6, 0.95],
      group: 'Side yard',
      drifts: [
        {
          x: garageX1 + sideRoom / 2,
          z: garageZ0 - 1.4,
          radiusMeters: radius,
          count: fitCount(Math.PI * radius * radius, 1.3, 8),
        },
      ],
    });
  }

  return {
    segment: {
      scale: { description: 'single garage bay', imageFraction: garageWidth / width, meters: garageWidth },
      site: {
        widthMeters: width,
        lengthMeters: length,
        orientation: 'the street runs along the +Z boundary; the house faces it',
      },
      observations: { dayOfYear: 172, hour: 10, minute: 30, latitudeDegrees: 42, weather: 'clear' },
      // The template is generated rather than observed, so it declares no image regions. Everything
      // downstream references world coordinates directly, which is exactly what a stage may do when
      // it has grounds for it.
      regions: [],
      unmodelled: ['fencing', 'street furniture', 'vehicles'],
      notes:
        'Generated residential lot rather than a reconstruction: a detached house with an attached ' +
        'garage set back from the street, a blacktop drive, a front walk, a rear terrace, ' +
        'foundation planting and a boundary screen.',
    },

    terrain: {
      elevationAmplitude: 0.25,
      roughness: 0.2,
      // A real lot is not a plane: it crowns slightly under the house and falls to the street so
      // that water leaves. Small numbers, but they are what stop the ground reading as a table.
      controlPoints: [
        {
          x: (mainX0 + mainX1) / 2,
          z: (mainZ0 + houseFrontZ) / 2,
          elevationMeters: 0.35,
          radiusMeters: Math.max(mainWidth, mainDepth) * 1.4,
          notes: 'house pad, crowned',
        },
        {
          x: driveCenter,
          z: hl - 0.5,
          elevationMeters: -0.28,
          radiusMeters: clamp(length * 0.32, 6, 22),
          notes: 'falls to the street for drainage',
        },
        {
          x: -hw + sideMargin,
          z: screenZ,
          elevationMeters: 0.12,
          radiusMeters: clamp(width * 0.4, 5, 18),
        },
      ],
      notes: 'Near-flat, as the template promises: a crowned pad and a fall of about 0.6 m to the street.',
    },

    surfaces: {
      regions: [
        {
          id: 'surface-drive',
          material: 'blacktop',
          // Flared at the street where a car turns in, straight where it meets the garage door.
          polygon: [
            [driveCenter - driveHalf, garageZ0 + garageDepth],
            [driveCenter + driveHalf, garageZ0 + garageDepth],
            [driveCenter + driveHalf + driveFlare, hl],
            [driveCenter - driveHalf - driveFlare, hl],
          ],
          featherMeters: 0.08,
          notes: 'asphalt drive, crisp edge',
        },
        {
          id: 'surface-foundation-bed',
          material: 'mulch',
          polygon: [
            [mainX0 - 1.9, houseFrontZ],
            [mainX1 - 0.2, houseFrontZ],
            [mainX1 - 0.2, houseFrontZ + bedDepth],
            [mainX0 - 1.9, houseFrontZ + bedDepth],
          ],
          featherMeters: 0.45,
          notes: 'foundation bed; soft edge where it meets mown turf',
        },
        {
          id: 'surface-rear-bed',
          material: 'mulch',
          polygon: [
            [mainX0 - 0.6, rearBedZ0],
            [mainX1 + 0.6, rearBedZ0],
            [mainX1 + 0.6, rearBedZ1],
            [mainX0 - 0.6, rearBedZ1],
          ],
          featherMeters: 0.5,
        },
      ],
      notes: 'Lawn is the base layer and is not painted; only the drive and the beds are.',
    },

    structures: {
      structures: [
        {
          id: 'site-house',
          kind: 'building',
          name: 'House',
          wallColor: '#cfc6b6',
          roofColor: '#5a5b60',
          clearanceMeters: 0.8,
          // Three masses because there are three roofs. One box could not express a garage whose
          // eave is lower than the house's, and one ridge could not honestly span the pair.
          masses: [
            {
              name: 'main range',
              polygon: rect(mainX0, mainZ0, mainX1, houseFrontZ),
              heightMeters: eave,
              roof: 'gable',
              roofRiseMeters: clamp(mainDepth * 0.3, 1.1, 3),
            },
            {
              name: 'attached garage',
              polygon: rect(garageX0, garageZ0, garageX1, houseFrontZ),
              heightMeters: garageEave,
              roof: 'gable',
              roofRiseMeters: clamp(garageDepth * 0.26, 0.8, 2),
              // Turned across, so the garage gable faces the street the way an attached bay does.
              ridgeCrosswise: true,
            },
            {
              name: 'porch',
              polygon: rect(
                porchCenter - porchWidth / 2,
                houseFrontZ,
                porchCenter + porchWidth / 2,
                houseFrontZ + porchDepth,
              ),
              heightMeters: clamp(eave * 0.62, 2.2, 3),
              roof: 'shed',
              roofRiseMeters: 0.45,
              baseMeters: 0.15,
            },
          ],
          notes: 'Two-storey range where the lot is deep enough, single-storey garage, covered porch.',
        },
        {
          id: 'site-terrace',
          kind: 'paving',
          name: 'Rear terrace',
          polygon: rect(mainX0 + 0.4, patioZ0, mainX1 - 0.4, patioZ1),
          paving: 'flagstone',
          clearanceMeters: 0,
        },
        {
          id: 'site-walk',
          kind: 'paving',
          name: 'Front walk',
          // Porch to the drive: the path a visitor actually takes, which is why it is not straight
          // out to the street.
          polygon: [
            [porchCenter - 0.7, houseFrontZ + porchDepth],
            [porchCenter + 0.7, houseFrontZ + porchDepth],
            [driveCenter - driveHalf, houseFrontZ + porchDepth + 1.4],
            [driveCenter - driveHalf, houseFrontZ + porchDepth + 0.2],
          ],
          paving: 'concrete',
          clearanceMeters: 0,
        },
      ],
      notes: '',
    },

    flora: { plantings, notes: '' },
  };
}

/**
 * How many plants at `spacing` genuinely fit in `areaM2`.
 *
 * Hexagonal packing puts about `0.87 · s²` of ground under each plant; asking for that many would
 * leave dart-throwing no room to find a legal position, so this budgets `1.3 · s²` and lets the
 * placement engine fill it. Under-asking is the right side to err on — a mass that places every
 * member is a mass with no warning attached to it.
 */
function fitCount(areaM2: number, spacing: number, max: number): number {
  if (!(areaM2 > 0) || !(spacing > 0)) return 0;
  return Math.max(0, Math.min(max, Math.floor(areaM2 / (spacing * spacing * 1.3))));
}

/** An axis-aligned rectangle as a closed ring, wound counter-clockwise in (x, z). */
function rect(x0: number, z0: number, x1: number, z1: number): [number, number][] {
  return [
    [x0, z0],
    [x1, z0],
    [x1, z1],
    [x0, z1],
  ];
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}
